import React, { useState, useEffect } from 'react';
import { Linkedin, Users, Building2, ExternalLink } from 'lucide-react';
import { TEAM_MEMBERS } from '../innovher/TheTeamSection';
import SEO from '../SEO';

export const NETWORK_TIERS = [
  {
    id: 'tier-1',
    title: 'Corporate & Strategic Partners',
    subtitle: 'Strategic, Legal & Operational Capabilities',
    gridCols: 'grid-cols-2 md:grid-cols-4',
    partners: [
      {
        name: 'DLS',
        subtitle: 'Legal & Corporate Advisory',
        logo: '/logo/dls.webp',
      },
      {
        name: 'ASM',
        subtitle: 'Strategic Consulting',
        logo: '/logo/asm.webp',
      },
      {
        name: 'Valserve',
        subtitle: 'Valuation & Advisory',
        logo: '/logo/valserve.webp',
      },
      {
        name: 'Gexpress',
        subtitle: 'Global Logistics & Supply Chain',
        logo: '/logo/gexpress.webp',
      },
    ],
  },
  {
    id: 'tier-2',
    title: 'Industry & Entrepreneurship Alliances',
    subtitle: 'Chambers of Commerce, Angel & Industry Alliances',
    gridCols: 'grid-cols-2 md:grid-cols-4',
    partners: [
      {
        name: 'TiE',
        subtitle: 'The Indus Entrepreneurs',
        logo: '/logo/tie.webp',
      },
      {
        name: 'FiCCI flo',
        subtitle: 'FICCI Ladies Organisation',
        logo: '/logo/ficci.webp',
      },
      {
        name: 'AssoCHAM',
        subtitle: 'Apex Chamber of Commerce',
        logo: '/logo/assocham.webp',
      },
      {
        name: 'ISBA',
        subtitle: 'Indian STEPs & Incubators Association',
        logo: '/logo/isba.webp',
      },
    ],
  },
  {
    id: 'tier-3',
    title: 'Incubation, Government & Institutional Ecosystem',
    subtitle: 'Incubators, Innovation Hubs, Angel Networks & Foundations',
    gridCols: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
    partners: [
      {
        name: 'iStart',
        subtitle: 'Govt. of Rajasthan Startup Initiative',
        logo: '/logo/istart.webp',
      },
      {
        name: 'ACIC-VGU',
        subtitle: 'Atal Community Innovation Centre',
        logo: '/logo/acic-vgu.webp',
      },
      {
        name: 'PIEDS',
        subtitle: 'BITS Pilani Incubation Society',
        logo: '/logo/pieds.webp',
      },
      {
        name: 'iHUB',
        subtitle: 'Student Startup & Innovation Hub',
        logo: '/logo/ihub.webp',
      },
      {
        name: 'FAAD',
        subtitle: 'Early-Stage Angel Network',
        logo: '/logo/faad.webp',
      },
      {
        name: 'Easy Knowledge Club',
        subtitle: 'Knowledge & Learning Ecosystem',
        logo: '/logo/easy.webp',
      },
      {
        name: 'NEN Wadhwani foundation',
        subtitle: 'National Entrepreneurship Network',
        logo: '/logo/nen.webp',
      },
      {
        name: 'CIEU',
        subtitle: 'Council for International Economic Understanding',
        logo: '/partners/cieu.png',
        imgClassName: 'max-h-[85%]',
      },
      {
        name: 'VOSA',
        subtitle: 'VOSA Futurepreneurs',
        logo: '/partners/vosa.png',
        imgClassName: 'max-h-[85%]',
      },
      {
        name: 'DATA Ingenious Global Limited',
        subtitle: 'Enterprise Software & IT Solutions',
        logo: '/partners/data-ingenious.png',
        imgClassName: 'max-h-[90%]',
      },
      {
        name: 'enCODE',
        subtitle: 'Creative Learning Platform',
        logo: '/partners/encode.png',
        imgClassName: 'max-h-[80%]',
      },
    ],
  },
];

function PartnerCard({ partner }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="group relative rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-[#d42b92]/60 transition-all duration-300 p-5 flex flex-col justify-between overflow-hidden shadow-xl hover:shadow-[0_12px_36px_-10px_rgba(212,43,146,0.25)] hover:-translate-y-1">
      {/* Subtle ambient hover glow */}
      <div className="absolute -top-12 -right-12 w-28 h-28 rounded-full bg-[#d42b92]/0 group-hover:bg-[#d42b92]/20 blur-2xl transition-all duration-500 pointer-events-none" />

      {/* Logo container (Clean white background for sharp contrast) */}
      <div className="w-full h-24 sm:h-28 rounded-xl bg-white border border-white/20 p-3 sm:p-4 flex items-center justify-center overflow-hidden shadow-md group-hover:scale-[1.02] transition-transform duration-300">
        {!imgError && partner.logo ? (
          <img
            src={partner.logo}
            alt={partner.name}
            onError={() => setImgError(true)}
            className={`max-h-full max-w-full object-contain ${partner.imgClassName || ''}`}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full grid place-items-center font-bold text-slate-800 text-lg">
            {partner.name}
          </div>
        )}
      </div>

      {/* Partner Name & Subtitle */}
      <div className="mt-4 pt-3 border-t border-white/10 flex flex-col justify-between flex-1">
        <h3 className="text-base sm:text-lg font-bold text-white tracking-tight group-hover:text-[#ff8ec5] transition-colors leading-snug">
          {partner.name}
        </h3>
        {/* <p className="mt-1 text-xs text-white/50 leading-relaxed font-medium line-clamp-2">
          {partner.subtitle}
        </p> */}
      </div>
    </div>
  );
}

function NetworkMemberRow({ member }) {
  const [imgError, setImgError] = useState(false);

  return (
    <article className="group flex flex-col sm:flex-row items-start gap-5 sm:gap-7 py-8 sm:py-10 border-b border-white/10 last:border-b-0 transition-colors">
      <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-lg overflow-hidden shrink-0 border border-white/10 bg-white/[0.03] shadow-md group-hover:border-[#d42b92]/50 transition-colors">
        {!imgError && member.image ? (
          <img
            src={member.image}
            alt={member.name}
            onError={() => setImgError(true)}
            className="w-full h-full object-cover object-top filter grayscale contrast-105 group-hover:grayscale-0 transition-all duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[#d42b92]/20 via-black to-black text-[#ff8ec5] font-bold text-lg sm:text-xl">
            {member.fallbackInitials || member.initials}
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight group-hover:text-[#ff8ec5] transition-colors">
            {member.name}
          </h3>
          {member.linkedin && (
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${member.name} on LinkedIn`}
              className="text-white/40 hover:text-[#0077b5] transition-colors p-1 shrink-0"
            >
              <Linkedin className="w-4 h-4 fill-current" />
            </a>
          )}
        </div>

        <div className="mt-1 text-sm sm:text-[15px] font-semibold text-[#ff8ec5] tracking-tight">
          {member.designation}
        </div>

        <p className="mt-3 text-sm sm:text-base text-white/70 leading-relaxed font-normal">
          {member.intro}
        </p>
      </div>
    </article>
  );
}

export default function NetworkPage() {
  const [activeTab, setActiveTab] = useState('All');

  const categories = ['All', 'Strategic Partner', 'Core Team'];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const filteredMembers =
    activeTab === 'All'
      ? TEAM_MEMBERS
      : TEAM_MEMBERS.filter((m) => m.category === activeTab);

  return (
    <div className="min-h-screen bg-[#08080a] text-white">
      <SEO
        title="The Network | InnovHer"
        description="Connected to move ventures further. Explore the strategic partners, industry alliances, and innovation ecosystem across InnovHer."
        keywords="InnovHer network, DLS, ASM, Valserve, Gexpress, TiE, FICCI FLO, ASSOCHAM, ISBA, iStart, ACIC VGU, PIEDS, iHUB, FAAD, Wadhwani NEN, Jaipur"
      />

      {/* Hero / Header Section */}
      <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-14 sm:pb-16 border-b border-white/5 overflow-hidden">
        <div className="absolute top-1/3 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#d42b92]/10 blur-[180px] pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="reveal-up max-w-4xl">
            <div className="text-[#ff8ec5] text-xs font-semibold tracking-[0.3em] uppercase mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#d42b92] inline-block animate-pulse" />
              <span>The Network</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.08]">
              Connected to move <span className="text-[#d42b92]">ventures further.</span>
            </h1>

            <p className="mt-6 text-xl sm:text-2xl font-medium text-white/90 leading-snug">
              Venture building becomes more powerful when the right ecosystems work together.
            </p>

            <div className="mt-6 space-y-5 text-white/80 text-base sm:text-lg leading-relaxed font-normal">
              <p>
                InnovHer collaborates with curated business networks, incubators, investor communities, institutions and strategic partners that extend the opportunities available to its ventures. These relationships create pathways for venture discovery, specialist support, market access, co-investment, partnerships and growth.
              </p>
              <p className="text-white/90 font-medium">
                The emphasis is not on building the largest network, but on building one that participates.
              </p>
            </div>
          </div>

          {/* Ecosystem Network Partners Section */}
          <div className="mt-16 space-y-16">
            {/* <div className="flex items-center gap-3 border-b border-white/10 pb-4">
              <div className="w-8 h-8 rounded-lg bg-[#d42b92]/15 border border-[#d42b92]/30 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-[#ff8ec5]" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                  Ecosystem Partners &amp; Alliances
                </h2>
                <p className="text-xs sm:text-sm text-white/50">
                  Institutions, corporate partners and networks moving ventures forward
                </p>
              </div>
            </div> */}

            {/* Sequence Rows */}
            {NETWORK_TIERS.map((tier, idx) => (
              <div key={tier.id} className="reveal-up space-y-6">
                {/* <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 border-b border-white/10 pb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono font-bold text-[#ff8ec5] px-2 py-0.5 rounded bg-white/[0.04] border border-white/10">
                      0{idx + 1}
                    </span>
                    <h3 className="text-lg sm:text-xl font-bold text-white tracking-wide">
                      {tier.title}
                    </h3>
                  </div>
                  <span className="text-xs text-white/45 font-medium">
                    {tier.subtitle}
                  </span>
                </div> */}

                <div className={`grid ${tier.gridCols} gap-4 sm:gap-6`}>
                  {tier.partners.map((partner) => (
                    <PartnerCard key={partner.name} partner={partner} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Strategic Partners & Advisors Directory */}
          {/* <div className="mt-24 pt-16 border-t border-white/10">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-4 reveal-up">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[#ff8ec5]" />
                <span className="text-sm font-bold text-white tracking-wide">
                  Strategic Advisors &amp; Venture Mentors ({filteredMembers.length})
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {categories.map((cat) => {
                  const active = activeTab === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setActiveTab(cat)}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider transition-all ${active
                        ? 'bg-[#d42b92] text-white shadow-md shadow-[#d42b92]/20'
                        : 'border border-white/10 bg-white/[0.02] text-white/60 hover:text-white hover:border-white/20'
                        }`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 divide-y divide-white/10">
              {filteredMembers.map((member) => (
                <NetworkMemberRow key={member.id} member={member} />
              ))}
            </div>
          </div> */}
        </div>
      </section>
    </div>
  );
}
